import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Slider,
  Switch,
  FormControlLabel,
  Autocomplete,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Schema as SchemaIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  ColumnSummary,
  SEMANTIC_ROLES,
} from '../../types/unifiedSchema';
import { useUnifiedSchema } from '../../hooks/useUnifiedSchema';
import TableSidebar from './TableSidebar';
import TableDetailTabs from './TableDetailTabs';
import RelationshipDiagram from '../RelationshipDiagram';

interface UnifiedSchemaViewProps {
  batchId: string;
  schema: ReturnType<typeof useUnifiedSchema>;
  /** Called when the user changes the selected table or tab */
  onViewChange?: (selectedTableIndex: number, activeTabIndex: number) => void;
  /** Schema-level output structure (from definition.output_structure). Enables the Structure tab. */
  outputStructure?: Record<string, any> | null;
}

const UnifiedSchemaView: React.FC<UnifiedSchemaViewProps> = ({ batchId, schema, onViewChange, outputStructure }) => {
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showGlobalDiagram, setShowGlobalDiagram] = useState(false);

  // Notify parent when user changes table or tab
  const handleSelectTable = (index: number) => {
    setSelectedTableIndex(index);
    setActiveTabIndex(0); // reset to Overview when switching table
    onViewChange?.(index, 0);
  };

  const handleTabChange = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
    onViewChange?.(selectedTableIndex, tabIndex);
  };

  // Fire initial view context on mount so the chat knows what the user sees
  const onViewChangeRef = React.useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  useEffect(() => {
    onViewChangeRef.current?.(0, 0);
  }, []);

  const {
    summaries,
    mergedTables,
    editingColumn,
    setEditingColumn,
    closeColumnEditor,
    saveColumnEdit,
    fkReferenceOptions,
    handleUpdateTable,
    handleUpdateColumn,
    handleTogglePrimaryKey,
    handleUpdateJoins,
    openColumnEditor,
  } = schema;

  const selectedTable = mergedTables[selectedTableIndex];

  return (
    <Box>
      {/* Global ER Diagram toggle */}
      {summaries.length >= 2 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setShowGlobalDiagram(!showGlobalDiagram)}
          >
            <SchemaIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Relationship Diagram
            </Typography>
            <Chip label={`${summaries.length} tables`} size="small" variant="outlined" sx={{ mr: 1 }} />
            <IconButton size="small">
              {showGlobalDiagram ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={showGlobalDiagram}>
            <Box sx={{ px: 2, pb: 2 }}>
              <RelationshipDiagram
                tables={summaries}
                onTableClick={(tableIndex) => handleSelectTable(tableIndex)}
                height={380}
              />
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Master-detail layout */}
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          minHeight: 'calc(100vh - 300px)',
          overflow: 'hidden',
        }}
      >
        {/* Left sidebar: table list */}
        <TableSidebar
          tables={mergedTables}
          selectedIndex={selectedTableIndex}
          onSelect={handleSelectTable}
        />

        {/* Right content: tabbed detail view */}
        {selectedTable ? (
          <TableDetailTabs
            table={selectedTable}
            tableIndex={selectedTableIndex}
            batchId={batchId}
            summaries={summaries}
            activeTab={activeTabIndex}
            onTabChange={handleTabChange}
            onUpdateTable={handleUpdateTable}
            onUpdateColumn={handleUpdateColumn}
            onTogglePrimaryKey={handleTogglePrimaryKey}
            onUpdateJoins={handleUpdateJoins}
            onOpenColumnEditor={openColumnEditor}
            outputStructure={outputStructure}
          />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Select a table from the sidebar</Typography>
          </Box>
        )}
      </Paper>

      {/* Column Edit Dialog */}
      <Dialog open={!!editingColumn} onClose={closeColumnEditor} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Column: {editingColumn?.column.original_name || editingColumn?.column.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              options={SEMANTIC_ROLES}
              value={editingColumn?.column.semantic_role || 'unknown'}
              onChange={(_, newValue) =>
                setEditingColumn((prev) =>
                  prev
                    ? {
                        ...prev,
                        column: {
                          ...prev.column,
                          semantic_role: (newValue || 'unknown').toLowerCase().replace(/\s+/g, '_'),
                        },
                      }
                    : null
                )
              }
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setEditingColumn((prev) =>
                    prev
                      ? {
                          ...prev,
                          column: {
                            ...prev.column,
                            semantic_role: newInputValue.toLowerCase().replace(/\s+/g, '_'),
                          },
                        }
                      : null
                  );
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Semantic Role" fullWidth />
              )}
              getOptionLabel={(option) =>
                option.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
              }
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={editingColumn?.column.description || ''}
              onChange={(e) =>
                setEditingColumn((prev) =>
                  prev ? { ...prev, column: { ...prev.column, description: e.target.value } } : null
                )
              }
            />

            <Box>
              <Typography gutterBottom>PII Likelihood</Typography>
              <Slider
                value={(editingColumn?.column.pii_likelihood ?? 0) * 100}
                onChange={(_, value) =>
                  setEditingColumn((prev) =>
                    prev
                      ? { ...prev, column: { ...prev.column, pii_likelihood: (value as number) / 100 } }
                      : null
                  )
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={editingColumn?.column.is_primary_key || false}
                  onChange={(e) =>
                    setEditingColumn((prev) =>
                      prev
                        ? { ...prev, column: { ...prev.column, is_primary_key: e.target.checked } }
                        : null
                    )
                  }
                />
              }
              label="Primary Key"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editingColumn?.column.is_foreign_key || false}
                  onChange={(e) =>
                    setEditingColumn((prev) =>
                      prev
                        ? { ...prev, column: { ...prev.column, is_foreign_key: e.target.checked } }
                        : null
                    )
                  }
                />
              }
              label="Foreign Key"
            />

            {editingColumn?.column.is_foreign_key && (
              <Autocomplete
                freeSolo
                options={fkReferenceOptions}
                value={editingColumn?.column.fk_references || ''}
                onChange={(_, newValue) =>
                  setEditingColumn((prev) =>
                    prev
                      ? { ...prev, column: { ...prev.column, fk_references: newValue || '' } }
                      : null
                  )
                }
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    setEditingColumn((prev) =>
                      prev
                        ? { ...prev, column: { ...prev.column, fk_references: newInputValue } }
                        : null
                    );
                  }
                }}
                groupBy={(option) => option.split('.')[0]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="References (Table.Column)"
                    placeholder="e.g., patients.patient_id"
                    helperText="Select or type table.column"
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnEditor}>Cancel</Button>
          <Button variant="contained" onClick={saveColumnEdit}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedSchemaView;
